import React, { useMemo, useState } from "react";
import QuillEditor from "@/modules/Email/components/QuillEditor";

const FieldLabel: React.FC<{ label: string; required?: boolean }> = ({ label, required }) => (
  <label className="text-sm text-[#1E1E1E] font-[500]">
    {label}
    {required ? <span className="text-[#FF3B30]">*</span> : null}
  </label>
);

const InfoField: React.FC<{ label: string; value?: string; danger?: boolean }> = ({ label, value, danger }) => (
  <div className="flex flex-col gap-1 w-full">
    <label className="text-xs text-[#6C6C6C]">{label}</label>
    <div className={`h-[38px] px-3 flex items-center border border-gray-200 rounded-[6px] text-sm bg-[#FFFFFF] ${danger ? "text-[#DC2626]" : "text-[#1E1E1E]"}`}>
      {value || "-"}
    </div>
  </div>
);

const quillModules = {
  toolbar: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"]],
};

export type ResendOnboardEmailData = {
  reason?: string | null;
  clientName: string;
  role?: string | null;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  bodyHtml: string;
};

const ResendFailedOnboardEmailForm: React.FC<{
  data: ResendOnboardEmailData;
  onCancel: () => void;
  onSubmit: (payload: { to: string[]; cc: string[]; bcc: string[]; subject: string; body: string }) => Promise<void> | void;
}> = ({ data, onCancel, onSubmit }) => {
  const [to, setTo] = useState<string>(data.to?.join(", ") || "");
  const [cc, setCc] = useState<string>(data.cc?.join(", ") || "");
  const [bcc, setBcc] = useState<string>(data.bcc?.join(", ") || "");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = useMemo(() => {
    return !!to && !!data.subject && !!data.bodyHtml;
  }, [to, data.subject, data.bodyHtml]);

  return (
    <form
      className="p-6 flex flex-col gap-5"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        try {
          setSubmitting(true);
          const toArr = (to || "").split(",").map((s) => s.trim()).filter(Boolean);
          const ccArr = (cc || "").split(",").map((s) => s.trim()).filter(Boolean);
          const bccArr = (bcc || "").split(",").map((s) => s.trim()).filter(Boolean);
          await onSubmit({ to: toArr, cc: ccArr, bcc: bccArr, subject: data.subject, body: data.bodyHtml });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      <div className="text-[16px] font-[500] leading-[20px] text-[#1E1E1E]">The email failed to send. Please review and send again.</div>

      <div className="flex flex-col gap-1">
        <FieldLabel label="Reason" />
        <input type="text" readOnly value={data.reason || ""} className="h-[38px] px-3 border border-gray-200 rounded-[6px] text-sm text-[#1E1E1E] bg-[#F9FAFB] w-full" />
      </div>

      <p className="text-sm text-[#6C6C6C]"><span className="text-[#FF3B30]">*</span> indicates mandatory fields</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InfoField label="User name" value={data.clientName} />
        <InfoField label="Role" value={data.role || ""} />
      </div>

      <div className="flex flex-col gap-1">
        <FieldLabel label="To" required />
        <input
          type="text"
          placeholder="Enter email addresses"
          className="h[42px] px-3 border border-gray-200 rounded-[6px] focus:outline-none h-[42px]"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <FieldLabel label="Cc" />
          <input type="text" placeholder="Enter cc recipients" className="h-[42px] px-3 border border-gray-200 rounded-[6px] focus:outline-none" value={cc} onChange={(e) => setCc(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel label="Bcc" />
          <input type="text" placeholder="Enter bcc recipients" className="h-[42px] px-3 border border-gray-200 rounded-[6px] focus:outline-none" value={bcc} onChange={(e) => setBcc(e.target.value)} />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <FieldLabel label="Subject" required />
        <input type="text" disabled className="h-[42px] px-3 border border-gray-200 rounded-[6px] focus:outline-none" value={data.subject}  />
      </div>

      <div className="flex flex-col gap-1">
        <FieldLabel label="Email content" required />
        <div>
          <QuillEditor value={data.bodyHtml} onChange={() => {}} modules={quillModules} placeholder="Enter email content" className="min-h-[160px]"  />
        </div>
      </div>

      <div className="flex items-center justify-end gap-4 pt-2">
        <button type="button" onClick={onCancel} className="h-[36px] px-4 rounded-md bg-[#F5F5F5] text-[#1E1E1E] text-sm hover:bg-gray-100">
          Cancel
        </button>
        <button type="submit" disabled={!canSubmit || submitting} className="h-[36px] px-4 rounded-md text-white text-sm bg-[#73B5C5] hover:bg-[#5da7b8] disabled:opacity-50 disabled:cursor-not-allowed">
          {submitting ? "Sending..." : "Send again"}
        </button>
      </div>
    </form>
  );
};

export default ResendFailedOnboardEmailForm;
